import { useEffect } from 'react';
import {
  useClientConfig,
  useLocalForageContext,
  useAuthContext,
  useCustomerContext,
} from '../context';
import { useApolloClient } from '@apollo/react-hooks';
import { syncCustomerOrders } from './AsyncCustomerOrders';
import moment from 'moment';
import localforage from 'localforage';
import {
  getCustomersQuery,
  getCustomersQueryOptions,
} from '@msales/shared/graphql/queries/customer/getCustomers';
import { getCustomerDiscounts } from '@msales/shared/graphql/queries/product/getCustomerDiscounts';
import { getAllPaymentDeadlinesQuery } from '@msales/shared/graphql/queries/customer/getAllPaymentDeadlines';
import { getAllCustomerDiscountsQuery } from '@msales/shared/graphql/queries/customer/getAllCustomerDiscounts';

import {
  getOrderTypesQuery,
  getOrderTypesQueryOptions,
} from '@msales/shared/graphql/queries/orderType/getOrderTypes';
import { getNewCustomerLookupsQuery } from '@msales/shared/graphql/queries/newCustomer/getNewCustomerLookups';
import { lookupsStore } from './useNewCustomerLookupsOfflineData';
import { getAllSurveysQuery } from '@msales/shared/graphql/queries/survey/getSurveys';
import { getAllSurveyDistributionResultsQuery } from '@msales/shared/graphql/queries/survey/getAllSurveyDistributionResults';
import { getBatSubjektProductsQuery } from '@msales/shared/graphql/queries/survey/getBatSubjektProducts';
import { isAppLockedQuery } from '@msales/shared/graphql/queries/task/isAppLocked';
import { useOfflineModeContext } from '../context/offlineContext';
import { useInsertEventLog } from './useInsertEventLog';
import { notificationManager } from '../utils/notificationManager';
import gql from 'graphql-tag';
import { reportSyncError, reportSyncRecovery } from '../utils/sentryHelpers';
import { safeSetItem } from '../utils/storageQuotaGuard';
import { getFailedStore } from '../utils/offlineSyncHelpers';
import { deduplicateCustomers } from '../utils/activityPolicy';

const synchronizeQueuedBlockedCustomerApprovals = async (
  customers = [],
  allowBlockedCustomerApproval = false,
) => {
  const customerBlockedById = new Map(
    customers.filter(Boolean).map((customer) => [
      String(customer.id),
      Boolean(customer.blocked),
    ]),
  );

  if (!customerBlockedById.size) return;

  const updateOperations = (operations = []) =>
    operations.map((operation) => {
      const customerId = operation?.variables?.input?.customerID;
      const approvalReason = operation?.variables?.input?.approvalReason;
      const isCustomerBlocked = customerBlockedById.get(String(customerId));

      if (
        operation.type !== 'INSERT_ORDER' ||
        isCustomerBlocked === undefined
      ) {
        return operation;
      }

      const regularOrderInput = { ...operation.variables.input };
      if (isCustomerBlocked && allowBlockedCustomerApproval) {
        if (approvalReason === 'BLOCKED_CUSTOMER') return operation;
        regularOrderInput.approvalReason = 'BLOCKED_CUSTOMER';
      } else if (approvalReason === 'BLOCKED_CUSTOMER') {
        delete regularOrderInput.approvalReason;
      } else {
        return operation;
      }
      return {
        ...operation,
        variables: {
          ...operation.variables,
          input: regularOrderInput,
        },
      };
    });

  const stores = [
    localforage.createInstance({ name: 'OfflineOperations_orders' }),
    getFailedStore('orders'),
  ];

  await Promise.all(
    stores.map(async (store) => {
      const operations = (await store.getItem('operations')) || [];
      const updatedOperations = updateOperations(operations);

      if (
        updatedOperations.some(
          (operation, index) => operation !== operations[index],
        )
      ) {
        await store.setItem('operations', updatedOperations);
      }
    }),
  );
};

export const useCustomersOfflineData = () => {
  const {
    loadingCustomers: loading,
    setLoadingCustomers: setLoading,
    refetchCustomers: refetch,
    setRefetchCustomers: setRefetch,
  } = useLocalForageContext();

  const { authUser } = useAuthContext();
  const { customer: selectedCustomer, setCustomer } = useCustomerContext();
  const { getValue } = useClientConfig();
  const { insertEvent } = useInsertEventLog();

  const customersStore = localforage.createInstance({
    name: 'Customers',
  });
  const { networkState } = useOfflineModeContext();
  const apolloClient = useApolloClient();

  useEffect(() => {
    const FetchProcess = async () => {
      if (!refetch) await new Promise(r => setTimeout(r, 3000));

      const shouldAsync = async (refetch) => {
        try {
          const createdAt = await customersStore.getItem('createdAt');

          if (createdAt && !refetch) {
            const nodes = await customersStore.getItem('nodes');
            if (!nodes) {
              await customersStore.removeItem('createdAt');
              console.warn('[AsyncCustomers] No customer data found, forcing re-sync');
              reportSyncRecovery('customers', 'createdAt-without-data');
            }
          }

          const freshCreatedAt = await customersStore.getItem('createdAt');
          if (
            (refetch ||
              !freshCreatedAt ||
              (false &&
                moment().diff(
                  moment(freshCreatedAt, 'DD/MM/YYYY HH:mm:ss'),
                  'minutes',
                ) >= 20)) &&
            (!networkState || networkState.online) &&
            authUser &&
            !loading
          ) {
            return true;
          }
          if (refetch) setRefetch(false);
          return false;
        } catch (err) {
          console.error('Error in shouldAsync:', err);
          return false;
        }
      };

      const AsyncCustomers = async () => {
        try {
          setLoading(true);
          notificationManager.startSync('customers', 'Kupci');

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 70000);

          try {
            const { data, error } = await apolloClient.query({
              ...getCustomersQueryOptions(''),
              query: getCustomersQuery,
              fetchPolicy: 'no-cache',
              context: { fetchOptions: { signal: controller.signal } },
            });
            if (error) {
              throw new Error(error.message);
            }
            await customersStore.setItem('nodes', data?.customers?.nodes);

            // Donator keeps the active order in the cart after a block-status notice.
            // Update queued operations and the selected customer from the incoming
            // sync so the existing order follows the current block status.
            if (getValue('features.refreshSelectedCustomerAfterSync', false)) {
              try {
                await synchronizeQueuedBlockedCustomerApprovals(
                  data?.customers?.nodes,
                  getValue('features.allowBlockedCustomerApproval', false),
                );
              } catch (queueError) {
                console.warn(
                  'Unable to update queued blocked-customer orders:',
                  queueError,
                );
              }

              const refreshedCustomer = deduplicateCustomers(
                data?.customers?.nodes,
              ).find(
                (node) => String(node.id) === String(selectedCustomer?.id),
              );
              if (refreshedCustomer) {
                setCustomer({ ...selectedCustomer, ...refreshedCustomer });
              }
            }

            // All sub-queries are independent — run in parallel to cut sync time on slow connections
            const [surveyData] = await Promise.all([
              // Surveys — result needed for BAT check below
              apolloClient.query({
                query: getAllSurveysQuery,
                fetchPolicy: 'no-cache',
                context: { fetchOptions: { signal: controller.signal } },
              }).then(({ data: d, error: e }) => {
                if (e) throw new Error(e.message);
                return safeSetItem(customersStore, 'surveysNodes', d?.allSurveys).then(() => d);
              }).catch((e) => {
                console.warn('Failed to fetch surveys:', e);
                return null;
              }),

              // Payment deadlines
              apolloClient.query({ query: getAllPaymentDeadlinesQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } })
                .then(({ data: d }) => {
                  const paymentDeadlines = {};
                  (d?.allPaymentDeadlines || []).forEach((item) => {
                    if (item.paymentDeadline !== null) paymentDeadlines[item.customerId] = item.paymentDeadline;
                  });
                  return safeSetItem(customersStore, 'paymentDeadlines', paymentDeadlines);
                }).catch((e) => console.warn('Failed to fetch bulk payment deadlines:', e)),

              getValue('customers.fetchDiscounts', false)
                ? apolloClient.query({ query: getAllCustomerDiscountsQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } })
                    .then(({ data: d }) => {
                      const allDiscounts = {};
                      (d?.allCustomerDiscounts || []).forEach((disc) => {
                        if (!allDiscounts[disc.customerId]) allDiscounts[disc.customerId] = {};
                        const existing = allDiscounts[disc.customerId][disc.productId] || { discount1: 0, discount2: 0 };
                        allDiscounts[disc.customerId][disc.productId] = {
                          discount1: Math.max(existing.discount1, disc.discount1 || 0),
                          discount2: Math.max(existing.discount2, disc.discount2 || 0),
                        };
                      });
                      return safeSetItem(customersStore, 'customerDiscounts', allDiscounts);
                    }).catch((e) => console.warn('Failed to fetch bulk customer discounts:', e))
                : Promise.resolve(),

              // New customer lookups
              apolloClient.query({ query: getNewCustomerLookupsQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } })
                .then(({ data: d }) => Promise.all([
                  d?.oblikOrganiziranja ? safeSetItem(lookupsStore, 'oblikOrganiziranja', d.oblikOrganiziranja) : Promise.resolve(),
                  d?.suKategorije ? safeSetItem(lookupsStore, 'suKategorije', d.suKategorije) : Promise.resolve(),
                ])).catch((e) => console.warn('Error caching new customer lookups:', e)),

              // Payment options
              apolloClient.query({
                query: gql`query getPaymentOptions { paymentOptions { code description } }`,
                fetchPolicy: 'no-cache',
                context: { fetchOptions: { signal: controller.signal } },
              }).then(({ data: d }) =>
                d?.paymentOptions ? safeSetItem(customersStore, 'paymentOptions', d.paymentOptions) : Promise.resolve()
              ).catch((e) => console.warn('Error caching payment options:', e)),

              // Survey distribution results
              apolloClient.query({ query: getAllSurveyDistributionResultsQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } })
                .then(({ data: d, error: e }) => {
                  if (e) { console.warn('Error fetching survey distribution results:', e); return; }
                  return safeSetItem(customersStore, 'surveyDistributionResults', d?.allSurveyDistributionResults);
                }).catch((e) => console.warn('Error fetching survey distribution results:', e)),

              // Order types
              apolloClient.query({ ...getOrderTypesQueryOptions(), query: getOrderTypesQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } })
                .then(({ data: d }) => Promise.all([
                  safeSetItem(customersStore, 'giftClasses', d?.giftClasses),
                  safeSetItem(customersStore, 'orderTypes', d?.orderTypes),
                ])).catch((e) => console.warn('Error fetching order types:', e)),

              // Organizations
              apolloClient.query({ query: GET_ORGANIZATIONS, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } })
                .then(({ data: d }) => safeSetItem(customersStore, 'usersOrganizations', d?.usersOrganizations))
                .catch((e) => console.warn('Error fetching organizations:', e)),

              // Visit comments
              apolloClient.query({ query: GET_VISIT_COMMENTS, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } })
                .then(({ data: d }) => safeSetItem(customersStore, 'visitComments', d?.visitComments))
                .catch((e) => console.warn('Error fetching visit comments:', e)),

              // App locked
              apolloClient.query({ query: isAppLockedQuery, fetchPolicy: 'no-cache', context: { fetchOptions: { signal: controller.signal } } })
                .then(({ data: d }) => safeSetItem(customersStore, 'appLocked', d?.isAppLocked))
                .catch((e) => console.warn('Error fetching app locked:', e)),
            ]);

            clearTimeout(timeoutId);

            // BAT products depend on survey result — must run after surveys resolve
            const surveysForBatCheck =
              surveyData?.allSurveys ||
              (await customersStore.getItem('surveysNodes').catch(() => null)) ||
              [];
            const hasBatSurveys = surveysForBatCheck.some(s => s.bat === 'D');
            if (hasBatSurveys) {
              try {
                const { data: batData } = await apolloClient.query({
                  query: getBatSubjektProductsQuery,
                  fetchPolicy: 'no-cache',
                });
                const batMap = {};
                (batData?.batSubjektProducts || []).forEach(({ customerId, productId }) => {
                  if (!batMap[customerId]) batMap[customerId] = [];
                  batMap[customerId].push(String(productId));
                });
                await safeSetItem(customersStore, 'batSubjektProducts', batMap);
              } catch (batErr) {
                console.warn('Error fetching BAT subject products:', batErr);
              }
            }
            await customersStore.setItem(
              'createdAt',
              moment().format('DD/MM/YYYY HH:mm:ss'),
            );

            syncCustomerOrders(apolloClient).catch(() => {});

            insertEvent({
              eventCode: 'SYNC',
              remark: 'Kupci',
            });

            notificationManager.completeSync('customers', true);
            setRefetch(false);
          } catch (err) {
            clearTimeout(timeoutId);
            console.error('Error in AsyncCustomers:', err);
            reportSyncError(err, 'customers', networkState);

            let errorMessage = 'Greška prilikom učitavanja kupaca!';
            if (err.name === 'AbortError' || controller.signal.aborted) {
              errorMessage = 'Sinhronizacija kupaca trajala predugo. Provjerite konekciju i pokušajte ponovo.';
            } else if (err.networkError?.statusCode === 400) {
              errorMessage = 'Neispravni podaci za učitavanje kupaca';
            } else if (err.networkError?.statusCode >= 500) {
              errorMessage = 'Greška na serveru prilikom učitavanja kupaca';
            } else if (err.message?.includes('Firebase ID token')) {
              errorMessage = 'Greška autentifikacije';
            }

            notificationManager.completeSync('customers', false, errorMessage);
            setRefetch(false);
          }
        } finally {
          setLoading(false);
        }
      };

      const callback = async (refetch) => {
        try {
          const should = await shouldAsync(refetch);
          if (should) {
            await AsyncCustomers();
          }
        } catch (err) {
          console.error('Error in callback:', err);
        }
      };

      try {
        await callback(refetch);
        // setInterval(() => callback(refetch), 180000);
      } catch (err) {
        console.error('Error in FetchProcess:', err);
      }
    };

    FetchProcess();

    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, authUser, selectedCustomer?.id, setCustomer, getValue]);

  return null;
};

const GET_ORGANIZATIONS = gql`
  query getUsersOrganization {
    usersOrganizations {
      id
      name
      code
    }
  }
`;

const GET_VISIT_COMMENTS = gql`
  query getPredefinedVisitComments {
    visitComments {
      id
      name
    }
  }
`;
